import { EnvConfig, useAuthentication } from '@gripp/shared-logic';
import { useEffect, useState } from 'react';

/** A local pick or object url is already playable — no round trip needed. */
const isDirectPlaybackUrl = (url: string) =>
  url.startsWith('blob:') || url.startsWith('file:');

/** The API answers /video/:id with `{ downloadUrl }`, never the bytes. */
const isApiVideoUrl = (url: string) =>
  url.includes('/video/') &&
  (url.startsWith(EnvConfig.apiUrl) || url.startsWith('/'));

/** Resolve to a full API url (handles a relative /video/:id when apiUrl is set). */
const getVideoApiUrl = (url: string): string => {
  if (!url || url.startsWith('http')) return url;
  const base = EnvConfig.apiUrl?.replace(/\/$/, '') || '';
  return base ? `${base}${url.startsWith('/') ? url : `/${url}`}` : url;
};

export type VideoPlaybackUrlState = {
  /** null while resolving, or when there is nothing to resolve. */
  playbackUrl: string | null;
  resolveError: boolean;
};

/**
 * Turns a media url into something a player can consume, exchanging our
 * `/video/:id` endpoint for the short-lived SAS url it returns.
 *
 * Shared by the in-bubble player and the full-screen preview panel so the auth
 * header, the cancel-on-unmount guard and the direct-url shortcuts stay in one
 * place.
 */
export const useVideoPlaybackUrl = (url: string): VideoPlaybackUrlState => {
  const authentication = useAuthentication();
  const accessToken = authentication?.accessToken;
  const [playbackUrl, setPlaybackUrl] = useState<string | null>(() =>
    url && isDirectPlaybackUrl(url) ? url : null
  );
  const [resolveError, setResolveError] = useState(false);

  useEffect(() => {
    if (!url) {
      setPlaybackUrl(null);
      setResolveError(false);
      return;
    }
    setResolveError(false);
    if (isDirectPlaybackUrl(url)) {
      setPlaybackUrl(url);
      return;
    }
    if (!isApiVideoUrl(url)) {
      setPlaybackUrl(url);
      return;
    }
    const finalUrl = getVideoApiUrl(url);
    if (!finalUrl.startsWith('http')) {
      setResolveError(true);
      return;
    }
    if (authentication?.isLoading) {
      return;
    }
    let cancelled = false;
    const headers: HeadersInit = {};
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }
    fetch(finalUrl, { credentials: 'omit', headers })
      .then((res) => {
        if (!res.ok)
          return Promise.reject(new Error(`${res.status} ${res.statusText}`));
        return res.json();
      })
      .then((data: { downloadUrl?: string }) => {
        if (cancelled) return;
        if (data.downloadUrl) {
          setPlaybackUrl(data.downloadUrl);
        } else {
          setResolveError(true);
        }
      })
      .catch(() => {
        if (!cancelled) setResolveError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [url, accessToken, authentication?.isLoading]);

  return { playbackUrl, resolveError };
};
